import type { SearchParams } from '@/lib/http/api-url'
import { HttpError } from '@/lib/http/http-error'
import { sessionFetch } from '@/lib/http/session-fetch'

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Plain object serialised as a JSON request body. */
  json?: unknown
  /** Query string parameters appended to the URL. */
  searchParams?: SearchParams
}

export interface DownloadedFile {
  blob: Blob
  filename: string | null
}

async function parseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) return response.json()
  return response.text()
}

async function request<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const { json, searchParams, headers, ...init } = options

  const response = await sessionFetch(
    path,
    {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: json !== undefined ? JSON.stringify(json) : undefined,
    },
    searchParams,
  )

  if (!response.ok) {
    throw new HttpError(response.status, response.statusText, await parseBody(response))
  }

  if (response.status === 204) return undefined as TResponse
  return (await parseBody(response)) as TResponse
}

/**
 * `filename="audit-all-20260825T101500Z.zip"`, with or without quotes, and the
 * RFC 5987 `filename*=UTF-8''…` form some servers send instead.
 */
const ENCODED_FILENAME = /filename\*=UTF-8''([^";]+)/i
const PLAIN_FILENAME = /filename="?([^";]+?)"?(?:;|$)/i

function filenameFrom(disposition: string | null): string | null {
  if (!disposition) return null

  // Only the RFC 5987 `filename*=` form is percent-encoded. Decoding the plain form too
  // would mangle a literal `%` that the server never escaped.
  const encoded = ENCODED_FILENAME.exec(disposition)?.[1]
  if (encoded !== undefined) {
    try {
      return decodeURIComponent(encoded)
    } catch {
      return encoded
    }
  }

  return PLAIN_FILENAME.exec(disposition)?.[1] ?? null
}

export interface FileRequestOptions {
  searchParams?: RequestOptions['searchParams']
  signal?: AbortSignal
  headers?: HeadersInit
}

async function requestFile(
  path: string,
  options: FileRequestOptions = {},
): Promise<DownloadedFile> {
  const { searchParams, headers, signal } = options

  const response = await sessionFetch(
    path,
    {
      method: 'GET',
      signal,
      headers: { Accept: 'application/zip', ...headers },
    },
    searchParams,
  )

  if (!response.ok) {
    throw new HttpError(response.status, response.statusText, await parseBody(response))
  }

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('content-disposition')),
  }
}

export const httpClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST' }),
  put: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT' }),
  patch: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH' }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  getFile: (path: string, options?: FileRequestOptions) => requestFile(path, options),
}
